import { useState, useEffect } from 'react';
import { FileText, Image as ImageIcon, Download, Trash2, Eye, Calendar, User } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { DocumentUpload } from './DocumentUpload';
import { DocumentPreview } from './DocumentPreview';

interface Document {
  id: string;
  file_name: string;
  file_type: 'pdf' | 'image';
  mime_type: string;
  file_size: number;
  storage_path: string;
  document_type: string;
  description: string | null;
  uploaded_at: string;
  uploaded_by_profile?: {
    full_name: string;
  };
}

interface DocumentsTabProps {
  maintenanceLogId: string;
}

export function DocumentsTab({ maintenanceLogId }: DocumentsTabProps) {
  const toast = useToast();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [previewDocument, setPreviewDocument] = useState<Document | null>(null);

  const documentTypes = [
    'Invoice',
    'Receipt',
    'Work Order',
    'Inspection Report',
    'Photo - Before',
    'Photo - After',
    'Photo - Damage',
    'Other',
  ];

  useEffect(() => {
    loadDocuments();
  }, [maintenanceLogId]);

  async function loadDocuments() {
    try {
      const { data, error } = await supabase
        .from('maintenance_documents')
        .select(`
          *,
          uploaded_by_profile:profiles!maintenance_documents_uploaded_by_fkey(full_name)
        `)
        .eq('maintenance_log_id', maintenanceLogId)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }

  async function handleDownload(doc: Document) {
    try {
      const { data, error } = await supabase.storage
        .from('maintenance-documents')
        .download(doc.storage_path);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Document downloaded');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  }

  async function handleDelete(doc: Document) {
    if (!confirm(`Are you sure you want to delete "${doc.file_name}"?`)) {
      return;
    }

    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('maintenance-documents')
        .remove([doc.storage_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('maintenance_documents')
        .delete()
        .eq('id', doc.id);

      if (dbError) throw dbError;

      toast.success('Document deleted');
      loadDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  }

  const filteredDocuments = documents.filter(doc => 
    filterType === 'all' || doc.document_type === filterType
  );

  const getFileIcon = (fileType: string) => {
    return fileType === 'pdf' ? (
      <FileText className="w-12 h-12 text-red-600" />
    ) : (
      <ImageIcon className="w-12 h-12 text-blue-600" />
    );
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Upload Documents</h3>
        <DocumentUpload
          maintenanceLogId={maintenanceLogId}
          onDocumentsChange={loadDocuments}
        />
      </div>

      {/* Filter and Documents */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900">
            Documents ({filteredDocuments.length})
          </h3>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Types</option>
            {documentTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        {filteredDocuments.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No documents uploaded yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDocuments.map((doc) => (
              <div
                key={doc.id}
                className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                {/* File Icon/Thumbnail */}
                <div className="flex items-center justify-center h-32 bg-slate-50 rounded-lg mb-3">
                  {getFileIcon(doc.file_type)}
                </div>

                {/* File Info */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-900 truncate" title={doc.file_name}>
                    {doc.file_name}
                  </h4>
                  
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded font-medium">
                      {doc.document_type}
                    </span>
                    <span className="text-xs text-slate-500">
                      {formatFileSize(doc.file_size)}
                    </span>
                  </div>

                  {doc.description && (
                    <p className="text-sm text-slate-600 line-clamp-2">
                      {doc.description}
                    </p>
                  )}

                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Calendar className="w-3 h-3" />
                    {new Date(doc.uploaded_at).toLocaleDateString()}
                  </div>

                  {doc.uploaded_by_profile && (
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <User className="w-3 h-3" />
                      {doc.uploaded_by_profile.full_name}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setPreviewDocument(doc)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </button>
                  <button
                    onClick={() => handleDownload(doc)}
                    className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    title="Download"
                  >
                    <Download className="w-4 h-4 text-slate-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(doc)}
                    className="p-2 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewDocument && (
        <DocumentPreview
          document={previewDocument}
          onClose={() => setPreviewDocument(null)}
          onDownload={() => handleDownload(previewDocument)}
          onDelete={() => {
            handleDelete(previewDocument);
            setPreviewDocument(null);
          }}
        />
      )}
    </div>
  );
}
